import * as React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export interface ListSkeletonProps {
  rows?: number;
  className?: string;
}

/** Generic stacked-bar skeleton — the pattern copy-pasted across nearly every page's loading state. */
export function ListSkeleton({ rows = 4, className }: ListSkeletonProps) {
  return (
    <div className={cn("space-y-6 py-2", className)}>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex w-full items-center space-x-4">
          <div className="w-full space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

export interface TableSkeletonProps {
  rows?: number;
  columns: number;
  className?: string;
}

/** Skeleton shaped like the real table (rows x columns), for tables that keep their header visible while loading. */
export function TableSkeleton({ rows = 4, columns, className }: TableSkeletonProps) {
  return (
    <Table className={className}>
      <TableBody>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <TableRow key={rowIndex}>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <TableCell key={colIndex}>
                <Skeleton className="h-4 w-full" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export interface CardSkeletonProps {
  className?: string;
}

/** Matches StatCard's icon + value + label shape. */
export function CardSkeleton({ className }: CardSkeletonProps) {
  return (
    <div className={cn("space-y-4 rounded-xl border p-6", className)}>
      <div className="flex items-center gap-2">
        <Skeleton className="h-10 w-10 rounded-lg" />
        <Skeleton className="h-4 w-20" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
  );
}

export interface TextSkeletonProps {
  lines?: number;
  className?: string;
}

/** N lines of text skeleton, last line shorter — for cards, descriptions, detail pages. */
export function TextSkeleton({ lines = 1, className }: TextSkeletonProps) {
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          className={cn("h-4 w-full", index === lines - 1 && lines > 1 && "w-2/3")}
        />
      ))}
    </div>
  );
}
